"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { FLOATING_PANEL_HOST_ID } from "@/features/notifications/lib/floatingPanelHost"

/**
 * The sheet counterpart to `workspace-dialog` — same reasoning, same trade-offs, same fallback.
 * See that file's header for why these are scoped to the content column rather than the viewport,
 * and why the rail ends up visible but not interactive.
 *
 * The one thing worth repeating here: a right-side sheet scoped to the column slides in from the
 * column's edge, not the window's. That is the point — at 1366px the column is 1298 / 1034 / 938px
 * wide depending on which panel is open, so a viewport-anchored sheet lands under the shell chrome.
 *
 * Drop-in for `@/components/ui/sheet`: same exports, same props. Swap the import path.
 */

/** Resolved in an effect, not render — see `workspace-dialog`. */
function useWorkspaceHost(): HTMLElement | null {
  const [host, setHost] = React.useState<HTMLElement | null>(null)

  React.useEffect(() => {
    setHost(document.getElementById(FLOATING_PANEL_HOST_ID))
  }, [])

  return host
}

const WorkspaceHostContext = React.createContext<HTMLElement | null>(null)

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Portal>) => {
  const host = useWorkspaceHost()

  return (
    <WorkspaceHostContext.Provider value={host}>
      <SheetPrimitive.Portal container={host ?? undefined} {...props}>
        {children}
      </SheetPrimitive.Portal>
    </WorkspaceHostContext.Provider>
  )
}
SheetPortal.displayName = "WorkspaceSheetPortal"

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const host = React.useContext(WorkspaceHostContext)

  return (
    <SheetPrimitive.Overlay
      ref={ref}
      className={cn(
        host ? "absolute inset-0 z-50 bg-black/80" : "fixed inset-0 z-50 bg-black/80",
        className
      )}
      {...props}
    />
  )
})
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

// No `animate-in` / `slide-*` classes: tailwindcss-animate is NOT installed, so they compile to
// nothing. `transition ease-in-out` is kept because that one is plain Tailwind and does work.
const sheetVariants = cva(
  "z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b",
        bottom: "inset-x-0 bottom-0 border-t",
        left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
        right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
      },
      anchor: {
        // Anchored to the content column…
        workspace: "absolute",
        // …or to the viewport, when the column is not in the DOM.
        viewport: "fixed",
      },
    },
    defaultVariants: {
      side: "right",
      anchor: "viewport",
    },
  }
)

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    Omit<VariantProps<typeof sheetVariants>, "anchor"> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <WorkspaceSheetInner ref={ref} side={side} className={className} {...props}>
      {children}
    </WorkspaceSheetInner>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const WorkspaceSheetInner = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => {
  const host = React.useContext(WorkspaceHostContext)

  return (
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        sheetVariants({ side, anchor: host ? "workspace" : "viewport" }),
        className
      )}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  )
})
WorkspaceSheetInner.displayName = "WorkspaceSheetInner"

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col space-y-2 text-center sm:text-left", className)}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
